import { CheckCircle, CloudUpload, Description, GetApp } from '@mui/icons-material';
import { Alert, Box, Button, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { EAnalysisStatus } from '@sharedInterfaces/IEmployeeTalkState';
import { EEntityType } from '@sharedInterfaces/globalEnums';
import type { IAttachment, ICVUploadData } from '@sharedInterfaces/IEmployeeTalkState';
import type { ICVUploadComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
// import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import deleteAttachment from '@src/APIs/graphQl/Attachment/deleteAttachment';
import getAttachmentDownloadURL from '@src/APIs/graphQl/Attachment/getAttachmentDownloadURL';
import { useAttachmentUpload } from '@src/App/Components/Tables/AttachmentsTable/useAttachmentUpload';
// import type { AppState } from '@store/store';
import React, { useCallback, useEffect } from 'react';
// import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';
import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';
import { useEmployeeTalk } from './ETTEmployeeTalkProvider';

interface ETTOnboardingCVUploadProps extends ITemplateComponentProps {
    onValidationChange?: (isValid: boolean) => void;
    employeeTalkId: number;
    pageIndex?: number;
}

const ETTOnboardingCVUploadComponent: React.FC<ETTOnboardingCVUploadProps> = (props) => {
    // const lang = useSelector((state: AppState) => state.employee.language);
    // const langStrings = getLanguageTexts(lang).employeeTalk;
    // const employee = useSelector((state: AppState) => state.employee);
    // GraphQL für Attachment-Löschung verwenden

    const properties = props.component.properties as ICVUploadComponentProperties;

    // Use ETTEmployeeTalkProvider
    // const { uploadFile } = useEmployeeTalk(); // DEPRECATED: Backend handles upload
    const { getComponentData, updateComponentData, setManualMode, isReadOnlyForCurrentUser, talkState, isManualMode: isManualModeFromProvider } = useEmployeeTalk();

    const { uploading, uploadedAttachments, startUpload, removeAttachment } = useAttachmentUpload({
        entityId: props.employeeTalkId,
        entityType: EEntityType.EMPLOYEE_TALK,
        onUploadFinished: (attachment) => {
            console.log('CV uploaded:', attachment.title);
            // Backend automatically updates component state after successful upload
            // No frontend event needed - prevents race conditions
        },
    });

    const handleFileSelect = useCallback(
        (file: File) => {
            startUpload(file);
        },
        [startUpload],
    );

    // Get files from secure state with proper typing
    const componentData = getComponentData(props.component.id) as ICVUploadData | null;
    // const { secureState } = useEmployeeTalk();

    // Check componentData for files
    const savedFiles = componentData?.files || [];
    const allSavedFiles = savedFiles;

    // Combine uploaded and saved files
    const allAttachments = React.useMemo(() => {
        // Convert uploadedAttachments to IAttachment format
        const convertedUploaded: IAttachment[] = uploadedAttachments.map((att) => ({
            id: att.id,
            title: att.title,
            fileType: att.fileType,
            size:
                (att as { size?: number; fileSize?: number }).size ||
                (att as { size?: number; fileSize?: number }).fileSize ||
                0,
            uploadedAt: (att as { uploadedAt?: string }).uploadedAt || new Date().toISOString(),
        }));

        const combined = [...convertedUploaded, ...allSavedFiles];
        return combined.filter((attachment, index, self) => index === self.findIndex((a) => a.id === attachment.id));
    }, [uploadedAttachments, allSavedFiles]);

    const isManualMode = isManualModeFromProvider || componentData?.manualContinue === true;

    // Check if analysis has started after upload
    const hasUploadedFiles = allAttachments.length > 0;
    const analysisStarted = talkState?.analysis?.started === true;
    const showAnalysisWaitingBox = hasUploadedFiles && !analysisStarted && !isManualMode;

    // Notify parent about upload state for navigation control (only when actually changed)
    const prevFilesLength = React.useRef(0);
    useEffect(() => {
        const hasFiles = allAttachments.length > 0;
        if (prevFilesLength.current !== allAttachments.length && props.onChange) {
            props.onChange({ uploaded: hasFiles, files: allAttachments });
            prevFilesLength.current = allAttachments.length;
        }
    }, [allAttachments, props.onChange]);

    const renderEditContent = () => (
        <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
                CV Upload Einstellungen
            </Typography>
            <Stack spacing={2}>
                <Box>
                    <Typography variant="body2" gutterBottom>
                        Maximale Dateigröße (MB)
                    </Typography>
                    <input
                        type="number"
                        value={properties.maxFileSize}
                        onChange={(e) =>
                            props.onEdit?.({
                                ...props.component,
                                properties: {
                                    ...properties,
                                    maxFileSize: parseInt(e.target.value, 10) || 10,
                                },
                            })
                        }
                        min="1"
                        max="50"
                        style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" gutterBottom>
                        Erlaubte Dateiformate
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {properties.allowedFormats.map((format) => (
                            <Chip key={format} label={format.toUpperCase()} size="small" />
                        ))}
                    </Box>
                </Box>
            </Stack>
        </Box>
    );

    const renderViewContent = () => (
        <Box sx={{ width: '100%', textAlign: 'center' }}>
            <Box
                sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 2,
                }}
            >
                {allAttachments.length > 0 ? (
                    <CheckCircle sx={{ fontSize: 40, color: 'white' }} />
                ) : (
                    <CloudUpload sx={{ fontSize: 40, color: 'white' }} />
                )}
            </Box>

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 500 }}>
                {allAttachments.length > 0 ? 'Lebenslauf hochgeladen!' : 'Lebenslauf hochladen'}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                {allAttachments.length > 0
                    ? 'Ihr Lebenslauf wurde erfolgreich hochgeladen und wird analysiert.'
                    : 'Laden Sie Ihren Lebenslauf hoch, damit wir Ihre Skills automatisch erkennen können.'}
            </Typography>

            {/* Analysis Waiting Box */}
            {showAnalysisWaitingBox && (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2">
                            <strong>CV-Analyse wird vorbereitet...</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Bitte warten Sie einen Moment, während wir Ihren Lebenslauf für die Analyse vorbereiten.
                        </Typography>
                    </Alert>
                    <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
                </Box>
            )}

            {uploading && (
                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" gutterBottom>
                        Upload läuft...
                    </Typography>
                    <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
                </Box>
            )}

            {/* Content Error Alert */}
            {componentData?.contentError && (
                <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                    <Typography variant="body2">
                        <strong>Lebenslauf-Inhalt konnte nicht extrahiert werden</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Der hochgeladene Lebenslauf konnte nicht analysiert werden. Bitte laden Sie einen neuen Lebenslauf hoch.
                    </Typography>
                </Alert>
            )}

            {allAttachments.length === 0 && !isManualMode && (
                <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                    <Typography variant="body2">
                        <strong>Pflichtfeld:</strong> Der Upload Ihres Lebenslaufs ist erforderlich.
                    </Typography>
                </Alert>
            )}

            {allAttachments.length === 0 && !isManualMode && (
                <Paper
                    elevation={0}
                    sx={{
                        border: '2px dashed',
                        borderColor: 'primary.main',
                        borderRadius: 2,
                        p: 4,
                        mb: 3,
                        textAlign: 'center',
                        backgroundColor: 'rgba(64, 61, 219, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            backgroundColor: 'rgba(64, 61, 219, 0.1)',
                            borderColor: 'primary.dark',
                        },
                    }}
                    onClick={() => document.getElementById('cv-upload-input')?.click()}
                >
                    <input
                        id="cv-upload-input"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        style={{ display: 'none' }}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                                handleFileSelect(file);
                            }
                        }}
                    />

                    <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />

                    <Typography variant="h6" gutterBottom>
                        Datei hier ablegen oder klicken zum Auswählen
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                        Unterstützte Formate: PDF, DOC, DOCX (max. {properties.maxFileSize}MB)
                    </Typography>
                </Paper>
            )}

            {/* Manual Continue Option */}
            {allAttachments.length === 0 && !isManualMode && (
                <Button
                    variant="outlined"
                    size="medium"
                    onClick={async () => {
                        await setManualMode(true);
                        // Backend handles navigation after setting manual mode
                    }}
                    sx={{
                        textTransform: 'none',
                        borderRadius: 2,
                        px: 3,
                        py: 1,
                        borderColor: 'primary.main',
                        color: 'primary.main',
                        '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: 'rgba(64, 61, 219, 0.05)',
                        },
                    }}
                >
                    Manuelles Onboarding
                </Button>
            )}

            {/* Switch back to automatic mode */}
            {isManualMode && allAttachments.length === 0 && (
                <Box sx={{ mb: 3 }}>
                    <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                        <Typography variant="body2">
                            <strong>Manueller Modus aktiv</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Sie können jederzeit einen Lebenslauf hochladen, um automatische Vorschläge zu erhalten.
                        </Typography>
                    </Alert>
                    <Paper
                        elevation={0}
                        sx={{
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 2,
                            p: 4,
                            textAlign: 'center',
                            backgroundColor: 'rgba(64, 61, 219, 0.05)',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                backgroundColor: 'rgba(64, 61, 219, 0.1)',
                                borderColor: 'primary.dark',
                            },
                        }}
                        onClick={() => document.getElementById('cv-upload-input-manual')?.click()}
                    >
                        <input
                            id="cv-upload-input-manual"
                            type="file"
                            accept=".pdf,.doc,.docx"
                            style={{ display: 'none' }}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) {
                                    handleFileSelect(file);
                                }
                            }}
                        />
                        <CloudUpload sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            Doch CV hochladen
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Unterstützte Formate: PDF, DOC, DOCX (max. {properties.maxFileSize}MB)
                        </Typography>
                    </Paper>
                </Box>
            )}

            {/* Show uploaded files */}
            {allAttachments.map((attachment) => (
                <Paper key={attachment.id} elevation={1} sx={{ p: 2, mt: 3, textAlign: 'left' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Description color="primary" />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight={500}>
                                {attachment.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {attachment.fileType}
                            </Typography>
                        </Box>
                        <Chip label="Hochgeladen" color="success" size="small" icon={<CheckCircle />} />
                        <Button
                            size="small"
                            color="primary"
                            startIcon={<GetApp />}
                            onClick={async () => {
                                try {
                                    const url = await getAttachmentDownloadURL(attachment.id);
                                    window.open(url, '_blank');
                                } catch (error) {
                                    console.error('Download failed:', error);
                                }
                            }}
                            sx={{ mr: 1 }}
                        >
                            Download
                        </Button>
                        <Button
                            size="small"
                            color="error"
                            disabled={isReadOnlyForCurrentUser}
                            onClick={async () => {
                                try {
                                    // Lösche Attachment über GraphQL
                                    await deleteAttachment(attachment.id);

                                    // Entferne aus lokalem Hook-State
                                    removeAttachment(attachment.id);

                                    // Update component data
                                    const remainingSaved = allSavedFiles.filter((a) => a.id !== attachment.id);
                                    const remainingUploaded = uploadedAttachments.filter((a) => a.id !== attachment.id);
                                    
                                    // Convert remaining uploaded to IAttachment format
                                    const convertedRemainingUploaded: IAttachment[] = remainingUploaded.map((att) => ({
                                        id: att.id,
                                        title: att.title,
                                        fileType: att.fileType,
                                        size:
                                            (att as { size?: number; fileSize?: number }).size ||
                                            (att as { size?: number; fileSize?: number }).fileSize ||
                                            0,
                                        uploadedAt:
                                            (att as { uploadedAt?: string }).uploadedAt || new Date().toISOString(),
                                    }));

                                    const remainingFiles = [...convertedRemainingUploaded, ...remainingSaved];

                                    // Update component data with proper interface
                                    const updatedData: ICVUploadData = {
                                        uploaded: remainingFiles.length > 0,
                                        files: remainingFiles,
                                    };
                                    updateComponentData(props.component.id, updatedData);

                                    // Notify parent
                                    if (props.onChange) {
                                        props.onChange({
                                            uploaded: remainingFiles.length > 0,
                                            files: remainingFiles,
                                        });
                                    }
                                } catch (error) {
                                    console.error('Fehler beim Löschen der Datei:', error);
                                }
                            }}
                            sx={{ ml: 1 }}
                        >
                            Löschen
                        </Button>
                    </Box>
                </Paper>
            ))}
        </Box>
    );

    if (props.isEditing) {
        return <ETTBaseTemplateComponent {...props}>{renderEditContent()}</ETTBaseTemplateComponent>;
    }

    return renderViewContent();
};

export default ETTOnboardingCVUploadComponent;
